import {
  faArrowLeft,
  faCheck,
  faChevronDown,
  faChevronRight,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import React, { Fragment, useState } from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { Flow } from "src/api/flowTypes";
import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { FlowVersionStatus } from "src/clients/flow-api";

type Value = {
  id: string;
  versionId: string;
  flowSlug?: string | null;
  versionName?: string | null;
} | null;

type OnSelectValue = Required<Value> | null;

type Props = {
  selected: Value;
  onSelect: (value: OnSelectValue) => void;
  flows: Flow[];
  disabled?: boolean;
  clearable?: boolean;
  placeholder?: string;
};

type MenuMode = { type: "flow" } | { type: "version"; flow: Flow };
const ELEMENTS_MAX_HEIGHT = "300px";

export const FlowAndVersionDropdown: React.FC<Props> = ({
  flows,
  onSelect,
  selected,
  disabled,
  clearable = false,
  placeholder = "Select Decision Flow and version",
}) => {
  const [menuMode, setMenuMode] = useState<MenuMode>({ type: "flow" });

  const selectedFlow = flows.find((flow) => flow.id === selected?.id);
  const selectedVersion = selectedFlow?.versions?.find(
    (version) => version.id === selected?.versionId,
  );
  return (
    <Menu as="div" className="w-full max-w-full">
      {({ open }) => (
        <>
          <MenuButton
            className={twMerge(
              "font-inter-normal-12px",
              "flex h-8 w-full items-center justify-center rounded-lg",
              "border border-gray-200 bg-white",
              "ring-inset focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
              (!selectedFlow || !selectedVersion) && "text-gray-500",
              open &&
                "border-indigo-400 outline-none ring-2 ring-indigo-500/25",
              disabled && "cursor-not-allowed bg-gray-50 text-gray-500",
            )}
            data-loc="flow-and-version-dropdown"
            disabled={disabled}
          >
            <div className="flex w-full items-center gap-x-1 py-1.5 pl-3 pr-2">
              <div>
                {!selectedFlow || !selectedVersion ? (
                  placeholder
                ) : (
                  <span className="flex items-center gap-x-1">
                    <Icon
                      color={
                        selectedVersion.status === FlowVersionStatus.PUBLISHED
                          ? "text-green-400"
                          : "text-yellow-400"
                      }
                      icon={faCircle}
                      size="2xs"
                    />
                    {`${selectedFlow?.name}, ${selectedVersion?.name}`}
                  </span>
                )}
              </div>
              <div className="ml-auto flex h-auto items-center">
                <Icon color="text-gray-500" icon={faChevronDown} size="xs" />
                {clearable && selected && !disabled && (
                  <div
                    className="flex items-center justify-start"
                    data-loc="flow-and-version-dropdown-clear"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="mx-1.5 h-4 w-[1px] bg-gray-200" />
                    <div role="button" onClick={() => onSelect(null)}>
                      <Icon color="text-gray-500" icon={faTimes} size="xs" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </MenuButton>
          <AnimatePresence>
            {open && (
              <MenuItems
                key="flow-and-version-dropdown"
                anchor={{
                  to: "bottom start",
                  gap: 8,
                  padding: 12,
                }}
                as={MotionDropdownPanel}
                className={twJoin(
                  "z-50 rounded-lg bg-white py-2 shadow-lg ring-1 ring-gray-200 ring-opacity-5 focus:outline-none",
                  "overflow-y-auto text-gray-800 font-inter-normal-13px",
                  "w-[var(--button-width)]",
                  ELEMENTS_MAX_HEIGHT,
                )}
                data-loc="items"
                style={
                  {
                    "--anchor-max-height": ELEMENTS_MAX_HEIGHT,
                  } as React.CSSProperties
                }
                static
              >
                {menuMode.type === "flow" && (
                  <Fragment key="flows-list">
                    {flows.length !== 0 && (
                      <>
                        {flows.map((flow) => (
                          <MenuItem
                            key={flow.id}
                            as="div"
                            className="group flex h-12 cursor-pointer items-center justify-between px-4 hover:bg-gray-50"
                            data-loc={`select-${flow.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setMenuMode({
                                type: "version",
                                flow: flow,
                              });
                            }}
                          >
                            {flow.name}
                            <div className="flex gap-x-2">
                              {selected?.id === flow.id && (
                                <Icon
                                  color="text-indigo-500"
                                  icon={faCheck}
                                  size="xs"
                                />
                              )}
                              <div className="hidden group-hover:block">
                                <Icon
                                  color="text-gray-500"
                                  icon={faChevronRight}
                                  size="xs"
                                />
                              </div>
                            </div>
                          </MenuItem>
                        ))}
                      </>
                    )}
                  </Fragment>
                )}
                {menuMode.type === "version" && (
                  <FlowSection
                    key="flow-versions-list"
                    flow={menuMode.flow}
                    selected={selected}
                    onExit={() => setMenuMode({ type: "flow" })}
                    onSelect={onSelect}
                  />
                )}
              </MenuItems>
            )}
          </AnimatePresence>
        </>
      )}
    </Menu>
  );
};

const FlowSection: React.FC<{
  flow: Flow;
  selected: Value;
  onSelect: (value: OnSelectValue) => void;
  onExit: () => void;
}> = ({ onExit, onSelect, selected, flow }) => {
  return (
    <div
      className={twJoin(
        "flex min-h-0 flex-col overflow-hidden",
        ELEMENTS_MAX_HEIGHT,
      )}
      data-loc="flow-and-version-dropdown-versions"
    >
      <div className="flex min-h-[3rem] items-center pl-4 text-gray-800 font-inter-medium-13px">
        <Icon
          color="text-gray-500"
          icon={faArrowLeft}
          size="sm"
          onClick={onExit}
        />
        <span className="ml-2">{flow.name}</span>
      </div>
      <div className="grow overflow-y-auto">
        {flow.versions?.map((version) => {
          const isSelected = selected?.versionId === version.id;
          return (
            <MenuItem
              key={version.id}
              as="div"
              className="group flex h-12 cursor-pointer items-center justify-between px-4 font-inter-normal-13px hover:bg-gray-50"
              data-loc={`select-${version.id}`}
              onClick={() => {
                onSelect({
                  id: flow.id,
                  versionId: version.id,
                  flowSlug: flow.slug,
                  versionName: version.name,
                });
              }}
            >
              <span className="flex items-center gap-x-1">
                <Icon
                  color={
                    version.status === FlowVersionStatus.PUBLISHED
                      ? "text-green-400"
                      : "text-yellow-400"
                  }
                  icon={faCircle}
                  size="2xs"
                />
                {version.name}
              </span>
              {isSelected && (
                <Icon color="text-indigo-500" icon={faCheck} size="xs" />
              )}
            </MenuItem>
          );
        })}
      </div>
    </div>
  );
};
